/* eslint-disable react/no-array-index-key */
import {
    SwipeNavButton,
    SwipeNavContainer,
    SwipeNavLeftButton,
    SwipeNavList,
    SwipeNavRightButton,
    SwipeNavWrap,
} from "@affinity/components/SwipeNav/SwipeNavStyledComponents";
import Icon from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import React, { useEffect, useRef } from "react";

export type SwipeNavElement = {
    onClick: Function;
    value: string;
};
export type SwipeNavProps = {
    elementArray: SwipeNavElement[];
};

const SwipeNav = ({ elementArray }: SwipeNavProps) => {
    const swipeWrapRef = useRef<HTMLDivElement | null>(null);
    const swipeListRef = useRef<HTMLUListElement | null>(null);
    const swipeButtonLeftRef = useRef<HTMLButtonElement | null>(null);
    const swipeButtonRightRef = useRef<HTMLButtonElement | null>(null);
    let swipeNavTimeout: number | undefined;

    const moveButtonClick = (direction: "left" | "right") => {
        if (swipeWrapRef.current == null || swipeListRef.current == null) {
            return;
        }

        const currentScroll = swipeWrapRef.current.scrollLeft;
        const currentSwipeArea = swipeListRef.current.offsetWidth - swipeWrapRef.current.offsetWidth;

        let updateValue;
        switch (direction) {
            case "left":
                updateValue = currentScroll - swipeWrapRef.current.offsetWidth;
                if (updateValue < 0) {
                    updateValue = 0;
                }
                break;
            case "right":
                updateValue = currentScroll + swipeWrapRef.current.offsetWidth;
                if (updateValue > currentSwipeArea) {
                    updateValue = currentSwipeArea;
                }
                break;
        }
        updateScroll(updateValue);
    };

    const updateScroll = (value: number) => {
        if (swipeWrapRef.current != null) {
            const roundVal = Math.round(value);
            swipeWrapRef.current.scrollTo({
                left: roundVal,
                behavior: "smooth",
            });
            enableDisableButtons(value);
        }
    };

    const enableDisableButtons = (value: number) => {
        if (
            swipeButtonLeftRef.current == null ||
            swipeButtonRightRef.current == null ||
            swipeWrapRef.current == null ||
            swipeListRef.current == null
        ) {
            return;
        }

        const roundVal = Math.round(value);
        const currentSwipeArea = swipeListRef.current.offsetWidth - swipeWrapRef.current.offsetWidth;

        swipeButtonLeftRef.current.disabled = false;
        swipeButtonRightRef.current.disabled = false;
        if (roundVal <= 0) {
            swipeButtonLeftRef.current.disabled = true;
        }
        if (roundVal >= currentSwipeArea) {
            swipeButtonRightRef.current.disabled = true;
        }
    };

    const buttonFocus = (evt: React.SyntheticEvent<HTMLButtonElement>) => {
        if (
            swipeButtonLeftRef.current == null ||
            swipeButtonRightRef.current == null ||
            swipeWrapRef.current == null ||
            swipeListRef.current == null
        ) {
            return;
        }

        const linkElem = evt.target as HTMLButtonElement;
        const currentScroll = swipeWrapRef.current.scrollLeft;
        const focusX = linkElem.offsetLeft;
        const focusWidth = linkElem.offsetWidth;

        if (currentScroll + focusX + focusWidth > swipeWrapRef.current.offsetWidth) {
            const newScroll = focusX - focusWidth / 2;
            updateScroll(newScroll);
        } else if (currentScroll > 0 && focusX - focusWidth < currentScroll) {
            const newScroll = focusX - focusWidth * 2;
            updateScroll(newScroll);
        }
    };

    const scrollHandler = (evt: React.SyntheticEvent<HTMLDivElement>) => {
        if (swipeNavTimeout) {
            window.clearTimeout(swipeNavTimeout);
        }

        swipeNavTimeout = window.setTimeout(() => {
            if (swipeWrapRef.current == null) {
                return;
            }
            enableDisableButtons(swipeWrapRef.current.scrollLeft);
        }, 17);
    };

    const wheelHandler = (evt: WheelEvent) => {
        if (swipeWrapRef.current == null || swipeListRef.current == null) {
            return;
        }

        const currentSwipeArea = swipeListRef.current.offsetWidth - swipeWrapRef.current.offsetWidth;

        if (evt.deltaX === 0) {
            evt.preventDefault();
            const currentScroll = swipeWrapRef.current.scrollLeft;
            let updateVal = currentScroll - evt.deltaY;
            if (updateVal < 0) {
                updateVal = 0;
            }
            if (updateVal > currentSwipeArea) {
                updateVal = currentSwipeArea;
            }
            updateScroll(updateVal);
        }
    };

    useEffect(() => {
        swipeWrapRef.current?.addEventListener("wheel", wheelHandler, { passive: false });
        return () => {
            swipeWrapRef.current?.removeEventListener("wheel", wheelHandler);
        };
    }, [swipeWrapRef.current]);

    return (
        <SwipeNavContainer>
            <SwipeNavLeftButton onClick={() => moveButtonClick("left")} ref={swipeButtonLeftRef} type="button">
                <Icon title="Left" src={ChevronLeft} size={24} />
            </SwipeNavLeftButton>
            <SwipeNavWrap ref={swipeWrapRef} onScroll={scrollHandler}>
                <SwipeNavList ref={swipeListRef}>
                    {elementArray.map((element, index) => (
                        <li key={`SwipeNavListItem${index}`}>
                            <SwipeNavButton
                                onMouseDown={e => e.preventDefault()}
                                onClick={e => {
                                    e.preventDefault();
                                    element.onClick();
                                }}
                                onFocus={buttonFocus}
                                type="button"
                            >
                                <span>{element.value}</span>
                            </SwipeNavButton>
                        </li>
                    ))}
                </SwipeNavList>
            </SwipeNavWrap>
            <SwipeNavRightButton onClick={() => moveButtonClick("right")} ref={swipeButtonRightRef} type="button">
                <Icon title="Right" src={ChevronRight} size={24} />
            </SwipeNavRightButton>
        </SwipeNavContainer>
    );
};

export default SwipeNav;
